import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

import { Input } from 'user/components';
import { useFreecodeContext } from 'hooks/useContext';
import { Modal, Header, Body, Footer, FooterButton } from '../modalElements';
import GDPRModal from '../GDPRModal';
import FreecodeAuthModal from './FreecodeAuthModal/FreecodeAuthModal';
import services from 'api/services';

const { GDPRService, GDPRAgreementService } = services;

// Submit modal intended for use with challanges
export default function SubmitChallengeModal({ onCancel, contest, isOpen = true }) {
  const { user, userRole, isTicketAuth, isCommunityUser } = useSelector((state) => state.auth);

  const { setTest, test, sponsor } = useFreecodeContext();

  const [teamName, setTeamName] = useState(test?.hackathonTeamName || '');
  const [gdprRequired, setGdprRequired] = useState(true);
  const [gdprAgreement, setGdprAgreement] = useState(undefined);
  const [gdprLoading, setGdprLoading] = useState(true);

  useEffect(() => {
    if (sponsor?.type !== 'contestSponsor') return setGdprRequired(false);
    if (!userRole) return;

    // content is sponsored and requires GDPR consent
    const fetchSponsorGdprInfo = async () => {
      try {
        let hasAgreed = await GDPRService.hasCandidateCompanyConsent(sponsor.companyId);
        if (hasAgreed) return setGdprRequired(false);
        let agreement = await GDPRAgreementService.getCompanyGDPRAgreement(sponsor.companyId);
        setGdprAgreement(agreement);
        setGdprLoading(false);
      } catch (e) {
        toast.error(typeof e === 'string' ? e : e?.message || 'GDPR error');
      }
    };
    fetchSponsorGdprInfo();
  }, [userRole, sponsor]);

  const confirmGdpr = (value) => {
    setGdprLoading(true);

    const doConfirmGdpr = async () => {
      try {
        let gdprAgreementConsent = await GDPRService.setGDPR(
          sponsor.companyId,
          value ? { conditionalRetention: value } : {}
        );
        if (gdprAgreementConsent) {
          toast.success('GDPR agreed');
          setGdprRequired(false);
        }
      } catch (e) {
        toast.error(typeof e === 'string' ? e : e?.message || 'GDPR error');
      }
      setGdprLoading(false);
    };
    doConfirmGdpr();
  };

  const onSubmit = (stayOnPage) => {
    // TODO get the input and submit it
    toast.error('not implemented');
    if (contest.isHackathon && teamName) setTest((oldTest) => ({ ...oldTest, hackathonTeamName: teamName }));
    if (stayOnPage) onCancel();
  };

  const hackathonSubmit = () => (
    <Modal isOpen={isOpen}>
      <Header>Submit</Header>
      <Body loading={false}>
        <p>
          Are you sure you want to submit your solution? You can submit multiple times but you only compete with your
          last submission.
        </p>
        <p>Enter a team name or submit as yourself.</p>
        <Input
          type="text"
          autoFocus
          value={teamName}
          placeholder={user?.coderCommunityProfile?.name || user?.name || 'unknown'}
          onChange={(e) => setTeamName(e.target.value)}
        />
      </Body>
      <Footer>
        <FooterButton onClick={onCancel} label="Cancel" />
        <FooterButton onClick={() => onSubmit(true)} label="Submit and Continue" />
        <FooterButton onClick={() => onSubmit(false)} label="Submit and Exit" />
      </Footer>
    </Modal>
  );

  const challengeSubmit = () => (
    <Modal isOpen={isOpen}>
      <Header>Submit</Header>
      <Body loading={false}>
        <p>
          Are you sure you want to submit your solution? You can submit multiple times but you only compete with your
          last submission.
        </p>
      </Body>
      <Footer>
        <FooterButton onClick={onCancel} label="Cancel" />
        <FooterButton onClick={() => onSubmit(true)} label="Submit and Continue" />
        <FooterButton onClick={() => onSubmit(false)} label="Submit and Exit" />
      </Footer>
    </Modal>
  );

  return !userRole || isTicketAuth || !isCommunityUser ? (
    <FreecodeAuthModal onCancel={onCancel} />
  ) : gdprRequired ? (
    <GDPRModal onConfirm={(value) => confirmGdpr(value)} gdprAgreement={gdprAgreement} loading={gdprLoading} />
  ) : contest.isHackathon ? (
    hackathonSubmit()
  ) : (
    challengeSubmit()
  );
}
